<template>
  <div class="manuals-page" data-testid="manuals-page">
    <div
      v-if="iframeLoading"
      class="manuals-page__loader"
      data-testid="manuals-page__loader"
    >
      <loader />
    </div>
    <div
      v-else-if="loadState !== 'ready'"
      class="manuals-page__empty"
      data-testid="manuals-page__empty"
    >
      <div class="manuals-page__empty-icon" aria-hidden="true">
        <v-icon class="manuals-page__empty-glyph">mdi-book-open-page-variant-outline</v-icon>
      </div>
      <h2 class="manuals-page__empty-title font-weight-medium" data-testid="manuals-page__empty-title">
        {{ emptyCopy.title }}
      </h2>
      <p class="manuals-page__empty-message" data-testid="manuals-page__empty-message">
        {{ emptyCopy.message }}
      </p>
    </div>
    <iframe
      v-if="showIframe"
      ref="manualsFrame"
      :srcdoc="frameHtml"
      sandbox="allow-same-origin"
      class="manuals-page__frame"
      :class="{ 'manuals-page__frame--pending': iframeLoading }"
      title="Product manuals"
      data-testid="manuals-page__iframe"
      @load="onIframeLoad"
    />
  </div>
</template>

<script>
import loader from '@/views/common/Loader';
import {
  collectCssUrls,
  isSkippableUrl,
  manualsApiUrl,
  manualsPathFromHref,
  parseSrcset,
  replaceCssResource,
  resolveDocumentBaseUrl,
  serializeSrcset,
  shouldTreatAsCss,
} from './ManualsUtils';
import { MANUALS_API_PREFIX, MEDIA_SRC_ATTRS } from '../utils/constants';
import './Manuals.scss';

const EMPTY_COPY = {
  missing: {
    title: 'Manuals not found',
    message: 'No product manuals have been published for this client yet. Contact support if you expected to see them here.',
  },
  error: {
    title: 'Unable to load manuals',
    message: 'Something went wrong while loading the manuals. Try again in a few minutes.',
  },
};

export default {
  name: 'Manuals',
  components: {
    loader,
  },
  data() {
    return {
      iframeLoading: true,
      loadState: 'loading',
      frameHtml: '',
      currentPageUrl: '',
      objectUrls: [],
      assetCache: {},
      assetPromises: {},
      loadGeneration: 0,
    };
  },
  computed: {
    clientId() {
      return this.$store.state.client || this.$route.query.cid || '';
    },
    manualsProduct() {
      const path = this.$route.path || '';
      if (this.$constants.isManualsPath(path)) {
        const queryProduct = this.$route.query[this.$constants.MANUALS_PRODUCT_QUERY] || '';
        return this.$constants.resolveManualsProduct(`/${queryProduct}`);
      }
      return this.$constants.resolveManualsProduct(path);
    },
    manualsPath() {
      if (!this.clientId || !this.manualsProduct) return '';
      return `${MANUALS_API_PREFIX}/${this.clientId}/${this.manualsProduct}/index.html`;
    },
    manualsSrc() {
      if (!this.manualsPath) return '';
      return manualsApiUrl(this.manualsPath);
    },
    showIframe() {
      return Boolean(this.frameHtml) && this.loadState === 'ready';
    },
    emptyCopy() {
      return EMPTY_COPY[this.loadState] || EMPTY_COPY.missing;
    },
  },
  watch: {
    manualsPath: {
      immediate: true,
      handler() {
        this.probeManuals();
      },
    },
  },
  beforeDestroy() {
    this.loadGeneration += 1;
    this.revokeObjectUrls();
  },
  methods: {
    probeManuals() {
      this.loadManualsPage(this.manualsPath);
    },
    async loadManualsPage(path) {
      const generation = this.loadGeneration + 1;
      this.loadGeneration = generation;
      this.iframeLoading = true;
      this.loadState = 'loading';
      this.revokeObjectUrls();

      if (!this.clientId || !this.manualsProduct || !path) {
        this.frameHtml = '';
        this.loadState = 'missing';
        this.iframeLoading = false;
        return;
      }

      try {
        console.log('[Manuals]:', path);
        const response = await this.$store.dispatch('m_manuals/fetchPage', { path });
        if (generation !== this.loadGeneration) return;
        if (this.isManualsMissingResponse(response)) {
          this.frameHtml = '';
          this.loadState = 'missing';
          this.iframeLoading = false;
          return;
        }
        if (response.status >= 400) {
          this.frameHtml = '';
          this.loadState = 'error';
          this.iframeLoading = false;
          return;
        }
        this.currentPageUrl = manualsApiUrl(path);
        this.frameHtml = await this.prepareManualsHtml(response.data, this.currentPageUrl);
        if (generation !== this.loadGeneration) return;
        this.loadState = 'ready';
      } catch (err) {
        if (generation !== this.loadGeneration) return;
        this.frameHtml = '';
        if (this.isManualsMissingResponse(err && err.response)) {
          this.loadState = 'missing';
        } else {
          this.loadState = 'error';
        }
        this.iframeLoading = false;
      }
    },
    isManualsMissingResponse(response) {
      if (!response) return false;
      if (response.status === 404) return true;
      const body = typeof response.data === 'string' ? response.data : '';
      return body.includes('Manuals not found');
    },
    async resolveManualsAsset(href, baseUrl, { asCss = false, wait = true } = {}) {
      if (isSkippableUrl(href)) return href;
      const path = manualsPathFromHref(href, baseUrl || this.currentPageUrl || this.manualsSrc);
      if (!path) return href;
      if (this.assetCache[path]) return this.assetCache[path];
      if (this.assetPromises[path]) {
        return wait ? this.assetPromises[path] : href;
      }

      const treatAsCss = asCss || shouldTreatAsCss({ href: path });
      const promise = this.loadRewrittenAsset(path, href, treatAsCss);
      this.assetPromises[path] = promise;
      const url = await promise;
      this.assetCache[path] = url;
      return url;
    },
    async loadRewrittenAsset(path, fallbackHref, treatAsCss) {
      try {
        if (treatAsCss) {
          const response = await this.$store.dispatch('m_manuals/fetchPage', { path });
          if (!response || response.status >= 400) return fallbackHref;
          const cssUrl = manualsApiUrl(path);
          const rewritten = await this.rewriteCss(response.data, cssUrl);
          const url = URL.createObjectURL(new Blob([rewritten], { type: 'text/css' }));
          this.objectUrls.push(url);
          return url;
        }
        const response = await this.$store.dispatch('m_manuals/fetchAsset', { path });
        if (!response || response.status >= 400) return fallbackHref;
        const url = URL.createObjectURL(response.data);
        this.objectUrls.push(url);
        return url;
      } catch (_) {
        return fallbackHref;
      }
    },
    async rewriteCss(cssText, cssUrl) {
      const urls = collectCssUrls(cssText);
      const replacements = await Promise.all(urls.map(async (raw) => {
        const treatAsCss = shouldTreatAsCss({ href: raw });
        const next = await this.resolveManualsAsset(raw, cssUrl, {
          asCss: treatAsCss,
          wait: !treatAsCss,
        });
        return { raw, next };
      }));
      let result = String(cssText || '');
      replacements.forEach(({ raw, next }) => {
        if (next && next !== raw) {
          result = replaceCssResource(result, raw, next);
        }
      });
      return result;
    },
    async rewriteSrcset(value, baseUrl) {
      const entries = parseSrcset(value);
      if (!entries.length) return value;
      const rewritten = await Promise.all(entries.map(async (entry) => {
        const url = await this.resolveManualsAsset(entry.url, baseUrl);
        return { url: url || entry.url, descriptor: entry.descriptor };
      }));
      return serializeSrcset(rewritten);
    },
    async prepareManualsHtml(html, pageUrl) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const base = resolveDocumentBaseUrl(doc, pageUrl);
      [...doc.querySelectorAll('base')].forEach((el) => el.remove());

      await Promise.all([
        ...MEDIA_SRC_ATTRS.map(async ([tag, attr]) => {
          await Promise.all([...doc.querySelectorAll(`${tag}[${attr}]`)].map(async (el) => {
            const next = await this.resolveManualsAsset(el.getAttribute(attr), base);
            if (next) el.setAttribute(attr, next);
          }));
        }),
        Promise.all([...doc.querySelectorAll('img[srcset], source[srcset]')].map(async (el) => {
          const next = await this.rewriteSrcset(el.getAttribute('srcset'), base);
          if (next) el.setAttribute('srcset', next);
        })),
        Promise.all([...doc.querySelectorAll('image, use')].map(async (el) => {
          await Promise.all(['href', 'xlink:href'].map(async (attr) => {
            if (!el.hasAttribute(attr)) return;
            const next = await this.resolveManualsAsset(el.getAttribute(attr), base);
            if (next) el.setAttribute(attr, next);
          }));
        })),
        Promise.all([...doc.querySelectorAll('link[href]')].map(async (link) => {
          const href = link.getAttribute('href');
          const next = await this.resolveManualsAsset(href, base, {
            asCss: shouldTreatAsCss({
              href,
              rel: link.getAttribute('rel'),
              asType: link.getAttribute('as'),
            }),
          });
          if (next) link.setAttribute('href', next);
        })),
        Promise.all([...doc.querySelectorAll('style')].map(async (styleEl) => {
          styleEl.textContent = await this.rewriteCss(styleEl.textContent, pageUrl);
        })),
        Promise.all([...doc.querySelectorAll('[style]')].map(async (el) => {
          const style = el.getAttribute('style');
          if (!style || !/url\(/i.test(style)) return;
          el.setAttribute('style', await this.rewriteCss(style, pageUrl));
        })),
      ]);

      [...doc.querySelectorAll('a[href]')].forEach((anchor) => {
        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:')) return;
        const path = manualsPathFromHref(href, base);
        if (!path) return;
        anchor.setAttribute('href', '#');
        anchor.setAttribute('data-manuals-path', path);
      });

      return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
    },
    onIframeLoad() {
      this.iframeLoading = false;
      const doc = this.$refs.manualsFrame && this.$refs.manualsFrame.contentDocument;
      if (!doc) return;
      doc.addEventListener('click', this.onManualsDocClick);
    },
    onManualsDocClick(event) {
      const anchor = event.target && event.target.closest && event.target.closest('a');
      if (!anchor) return;
      const path = anchor.getAttribute('data-manuals-path');
      if (!path) return;
      event.preventDefault();
      this.loadManualsPage(path);
    },
    revokeObjectUrls() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = [];
      this.assetCache = {};
      this.assetPromises = {};
    },
  },
};
</script>
